import openaiBackendService from './openaiBackendService';
import openai from './openaiClient'; // Keep for fallback

// Flag to control whether to use backend or direct OpenAI
const USE_BACKEND = import.meta.env.VITE_USE_BACKEND !== 'false'; // Default to true

/**
 * Analyzes interview feedback and provides structured insights
 * Now uses backend API by default, with fallback to direct OpenAI
 * @param {string} interviewNotes - Raw interview notes
 * @param {string} candidateName - Candidate name
 * @param {string} position - Position being interviewed for
 * @returns {Promise<object>} Structured interview analysis
 */
export async function analyzeInterviewFeedback(interviewNotes, candidateName, position) {
  if (USE_BACKEND) {
    try {
      const result = await openaiBackendService.analyzeInterview({
        interviewNotes,
        candidateName,
        position,
      });
      
      // Transform backend response to match frontend expectations
      const analysis = result.analysis;
      return {
        overallRating: analysis.overall_rating,
        technicalRating: analysis.technical_rating,
        communicationRating: analysis.communication_rating,
        culturalFitRating: analysis.cultural_fit_rating,
        strengths: analysis.strengths,
        concerns: analysis.concerns,
        summary: analysis.summary,
        recommendation: analysis.recommendation,
        nextSteps: analysis.next_steps,
        tags: analysis.tags,
      };
    } catch (error) {
      console.warn('Backend interview analysis failed, falling back to direct OpenAI:', error.message);
      // Fallback to direct OpenAI call
    }
  }
  
  // Fallback to direct OpenAI if backend is disabled or failed
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [
        { 
          role: 'system', 
          content: 'You are an expert HR analyst who analyzes interview feedback to provide actionable insights and recommendations.'
        },
        { 
          role: 'user', 
          content: `Analyze this interview feedback for ${candidateName} applying for ${position}:

Interview Notes:
${interviewNotes}

Provide a structured analysis with insights and recommendations.`
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'interview_analysis_response',
          schema: {
            type: 'object',
            properties: {
              overallRating: { type: 'number' },
              technicalRating: { type: 'number' },
              communicationRating: { type: 'number' },
              culturalFitRating: { type: 'number' },
              strengths: {
                type: 'array',
                items: { type: 'string' }
              },
              concerns: {
                type: 'array',
                items: { type: 'string' }
              },
              summary: { type: 'string' },
              recommendation: { type: 'string' },
              nextSteps: {
                type: 'array',
                items: { type: 'string' }
              },
              tags: {
                type: 'array',
                items: { type: 'string' }
              }
            },
            required: [
              'overallRating', 'technicalRating', 'communicationRating',
              'culturalFitRating', 'strengths', 'concerns', 'summary',
              'recommendation', 'nextSteps', 'tags'
            ],
            additionalProperties: false,
          },
        },
      },
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error analyzing interview feedback:', error);
    throw new Error(`Interview analysis failed: ${error.message}`);
  }
}

/**
 * Generates follow-up questions based on interview analysis
 * @param {object} analysisResult - Interview analysis results
 * @param {string} position - Position
 * @returns {Promise<Array>} Array of follow-up questions
 */
export async function generateFollowUpQuestions(analysisResult, position) {
  if (USE_BACKEND) {
    try {
      // Generate questions using the backend service
      const result = await openaiBackendService.generateQuestions({
        candidateSummary: `Based on interview analysis: ${analysisResult?.summary}`,
        jobPosition: position,
        keySkills: analysisResult?.tags || [],
      });
      
      // Transform to follow-up question format
      const questions = result.questions;
      const allQuestions = [
        ...questions.technical_questions?.map(q => ({
          question: q.question,
          purpose: `Technical follow-up (${q.difficulty})`,
          category: 'technical'
        })) || [],
        ...questions.behavioral_questions?.map(q => ({
          question: q.question,
          purpose: q.purpose,
          category: 'behavioral'
        })) || [],
      ];
      
      return { questions: allQuestions };
    } catch (error) {
      console.warn('Backend follow-up generation failed, falling back to direct OpenAI:', error.message);
      // Fallback to direct OpenAI call
    }
  }
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [
        { 
          role: 'system', 
          content: 'You are an HR expert who generates thoughtful follow-up interview questions based on initial interview analysis.'
        },
        { 
          role: 'user', 
          content: `Based on this interview analysis for a ${position} candidate, generate relevant follow-up questions:

Strengths: ${analysisResult?.strengths?.join(', ')}
Concerns: ${analysisResult?.concerns?.join(', ')}
Summary: ${analysisResult?.summary}

Generate questions that address concerns and explore strengths further.`
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'followup_questions_response',
          schema: {
            type: 'object',
            properties: {
              questions: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    question: { type: 'string' },
                    purpose: { type: 'string' },
                    category: { type: 'string' }
                  },
                  required: ['question', 'purpose', 'category']
                }
              }
            },
            required: ['questions'],
            additionalProperties: false,
          },
        },
      },
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error generating follow-up questions:', error);
    throw new Error(`Follow-up questions generation failed: ${error.message}`);
  }
}

/**
 * Generates AI-powered insights for hiring decision
 * Now uses backend comprehensive evaluation service
 * @param {object} candidateProfile - Full candidate profile
 * @param {Array} interviewResults - All interview results
 * @param {string} position - Target position
 * @returns {Promise<object>} Hiring decision insights
 */
export async function generateHiringInsights(candidateProfile, interviewResults, position) {
  if (USE_BACKEND) {
    try {
      // Use the comprehensive backend service for hiring insights
      const result = await openaiBackendService.getHiringInsights({
        cvText: candidateProfile?.cvText || candidateProfile?.aiSummary,
        jobPosition: position,
        jobRequirements: candidateProfile?.jobRequirements || '',
        interviewNotes: interviewResults?.map(r => r.feedback || r.notes).join('\n\n'),
        candidateName: candidateProfile?.name,
      });
      
      // Transform backend response to match frontend expectations
      const recommendation = result.overallRecommendation;
      return {
        hiringRecommendation: recommendation.recommendation,
        confidenceLevel: recommendation.finalScore / 10, // Convert to 0-10 scale
        riskFactors: result.cvAnalysis?.recommendations
          ? result.cvAnalysis.recommendations.filter(r => r.recommendation_type === 'concern').map(r => r.description)
          : [],
        successPredictors: result.cvAnalysis?.key_highlights || [],
        salaryRecommendation: {
          range: 'To be determined based on market analysis',
          justification: recommendation.reasoning,
        },
        onboardingRecommendations: result.cvAnalysis?.recommendations
          ? result.cvAnalysis.recommendations.filter(r => r.recommendation_type === 'development').map(r => r.description)
          : [],
        executiveSummary: result.cvAnalysis?.ai_summary || '',
      };
    } catch (error) {
      console.warn('Backend hiring insights failed, falling back to direct OpenAI:', error.message);
      // Fallback to direct OpenAI call
    }
  }
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [
        { 
          role: 'system', 
          content: 'You are a senior HR consultant providing data-driven hiring recommendations based on comprehensive candidate evaluation.'
        },
        { 
          role: 'user', 
          content: `Provide hiring insights for this candidate applying for ${position}:

Candidate Profile:
- Name: ${candidateProfile?.name}
- AI Score: ${candidateProfile?.aiScore}
- Skills Match: ${candidateProfile?.skillsMatch}%
- Experience Match: ${candidateProfile?.experienceMatch}%
- Key Skills: ${candidateProfile?.keySkills?.join(', ')}
- Summary: ${candidateProfile?.aiSummary}

Interview Results:
${interviewResults?.map(result => `
- ${result?.type}: Rating ${result?.rating}/5
- Feedback: ${result?.feedback}
`)?.join('\n')}

Provide comprehensive hiring insights and recommendation.`
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'hiring_insights_response',
          schema: {
            type: 'object',
            properties: {
              hiringRecommendation: { type: 'string' },
              confidenceLevel: { type: 'number' },
              riskFactors: {
                type: 'array',
                items: { type: 'string' }
              },
              successPredictors: {
                type: 'array',
                items: { type: 'string' }
              },
              salaryRecommendation: {
                type: 'object',
                properties: {
                  range: { type: 'string' },
                  justification: { type: 'string' }
                },
                required: ['range', 'justification']
              },
              onboardingRecommendations: {
                type: 'array',
                items: { type: 'string' }
              },
              executiveSummary: { type: 'string' }
            },
            required: [
              'hiringRecommendation', 'confidenceLevel', 'riskFactors',
              'successPredictors', 'salaryRecommendation', 'onboardingRecommendations',
              'executiveSummary'
            ],
            additionalProperties: false,
          },
        },
      },
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error generating hiring insights:', error);
    throw new Error(`Hiring insights generation failed: ${error.message}`);
  }
}